import HistoryIcon from '@mui/icons-material/History'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomButton from '@/components/inputs/CustomButton'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type { BidDetailQueryParams } from '@/features/bidding/hooks/bidDetail/useBidDetailQueryParams'

export type BiddingVersionsButtonProps = Readonly<{
  marketProgram: MarketProgram
  bidType: BidType
  queryParams: BidDetailQueryParams
}>

/**
 * CTA that navigates to the legacy bid history view (`/bidding/view`),
 * carrying the current context (portfolio, delivery day, market program,
 * bid type) so the user lands on the matching versions.
 *
 * Versions were removed from the new monitoring view, but remain accessible
 * through this button.
 */
export default function BiddingVersionsButton({ marketProgram, bidType, queryParams }: BiddingVersionsButtonProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleClick = () => {
    const params = new URLSearchParams()
    params.set('marketProgram', marketProgram)
    params.set('bidType', bidType)
    if (queryParams.portfolioCode) {
      params.set('portfolioCode', queryParams.portfolioCode)
    }
    if (queryParams.deliveryDay) {
      params.set('deliveryDay', queryParams.deliveryDay.toISODate())
    }
    if (queryParams.activationGroupUuid) {
      params.set('activationGroupUuid', queryParams.activationGroupUuid)
    }

    navigate(`/bidding/view?${params.toString()}`)
  }

  return (
    <CustomButton startIcon={<HistoryIcon />} variant="contained" onClick={handleClick}>
      {t('bidding.results.bidding_versions')}
    </CustomButton>
  )
}
